// React Query hooks for the admin teams api (web#38). All keys live under ['admin-teams'].

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api, ApiClientError } from '@/lib/api'
import { useRealtimeEvent } from '@/providers/RealtimeProvider'
import { buildListQueryString, realRevokeAdvisor } from '@/lib/adminApi.real'
import type {
  AdminTeamDetail,
  InviteResult,
  MoveMembersResult,
  SalesPeriod,
  TeamCandidate,
  TeamListParams,
} from '@/types/adminTeam'
import type { Data, Meta } from '@/types/apiData'

export const adminTeamKeys = {
  all: ['admin-teams'] as const,
  list: (params: TeamListParams) => [...adminTeamKeys.all, 'list', params] as const,
  detail: (id: string, period: SalesPeriod) => [...adminTeamKeys.all, 'detail', id, period] as const,
  mine: (period: SalesPeriod) => [...adminTeamKeys.all, 'mine', period] as const,
  candidates: (id: string, search: string) => [...adminTeamKeys.all, 'candidates', id, search] as const,
}

/** team_change (any entity) refreshes every team, company and team-cases query. Mount once, inside RealtimeProvider. */
export function useTeamEventInvalidation() {
  const qc = useQueryClient()
  useRealtimeEvent('team_change', () => {
    for (const queryKey of [adminTeamKeys.all, ['company'], ['team-cases']]) void qc.invalidateQueries({ queryKey })
  })
}

const TEAM_LIST_FIELDS = ['search', 'companyStatus', 'sortBy', 'sortDir', 'period'] as const

export async function fetchTeamList(params: Omit<TeamListParams, 'pageSize'> & { pageSize: number }) {
  const qs = buildListQueryString(params, TEAM_LIST_FIELDS)
  const { data, meta } = await api.get<Data<'listTeams'>, Meta<'listTeams'>>(`/api/teams?${qs}`)
  return { rows: data!, ...meta! }
}

export function useAdminTeams(params: TeamListParams) {
  return useQuery({
    queryKey: adminTeamKeys.list(params),
    queryFn: () => fetchTeamList(params),
    placeholderData: (prev) => prev,
  })
}

export function useAdminTeam(id: string | undefined, period: SalesPeriod) {
  return useQuery({
    queryKey: adminTeamKeys.detail(id ?? '', period),
    queryFn: async () => (await api.get<AdminTeamDetail>(`/api/teams/${id}?period=${period}`)).data!,
    enabled: !!id,
    // keep the old figures on screen while the other period loads (same team only)
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[2] === id ? prev : undefined),
    // 404 TEAM_NOT_FOUND is a final answer
    retry: (count, err) => !(err instanceof ApiClientError && err.code === 'TEAM_NOT_FOUND') && count < 3,
  })
}

/** The signed-in team leader's own team; the api resolves it from the session. */
export function useMyTeam(period: SalesPeriod) {
  return useQuery({
    queryKey: adminTeamKeys.mine(period),
    queryFn: async () => (await api.get<AdminTeamDetail>(`/api/team?period=${period}`)).data!,
    placeholderData: (prev) => prev,
    retry: (count, err) => !(err instanceof ApiClientError && err.code === 'TEAM_NOT_FOUND') && count < 3,
  })
}

export function useTeamCandidates(id: string | undefined, search: string) {
  return useQuery({
    queryKey: adminTeamKeys.candidates(id ?? '', search),
    queryFn: async () =>
      (await api.get<TeamCandidate[]>(`/api/teams/${id}/candidates?search=${encodeURIComponent(search)}`)).data ?? [],
    enabled: !!id,
    placeholderData: keepPreviousData,
  })
}

// Every mutation can change list rows, member counts and candidates, so invalidate the whole root.
function useTeamMutation<TVars, TData>(fn: (vars: TVars) => Promise<TData>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: adminTeamKeys.all }),
    // Invite already accepted or cancelled elsewhere: say so and refetch so the row goes away.
    onError: (err) => {
      if (!(err instanceof ApiClientError && err.code === 'INVITE_NOT_FOUND')) return
      toast.error('This invitation no longer exists')
      void qc.invalidateQueries({ queryKey: adminTeamKeys.all })
    },
  })
}

export const useRenameTeam = () =>
  useTeamMutation(async ({ id, name }: { id: string; name: string }) => {
    await api.patch(`/api/teams/${id}`, { name })
  })

export const useMoveMembers = () =>
  useTeamMutation(
    async ({ teamId, profileIds, note }: { teamId: string; profileIds: string[]; note?: string }) =>
      (await api.post<MoveMembersResult>(`/api/teams/${teamId}/members`, { profileIds, note })).data!,
  )

export const useSetTeamLeader = () =>
  useTeamMutation(async ({ teamId, profileId, note }: { teamId: string; profileId: string; note?: string }) => {
    await api.put(`/api/teams/${teamId}/leader`, { profileId, note })
  })

export const useInviteAdvisors = () =>
  useTeamMutation(
    async ({ teamId, ...body }: { teamId: string; emails: string[]; leaderEmail?: string }) =>
      (await api.post<InviteResult>(`/api/teams/${teamId}/invitations`, body)).data!,
  )

export const useRevokeMember = () => useTeamMutation((applicationId: string) => realRevokeAdvisor(applicationId))

export const useCreateTeam = () =>
  useTeamMutation(async (vars: { name: string }) => (await api.post<Data<'createTeam'>>('/api/teams', vars)).data!)

type InviteRef = { teamId: string; inviteId: string }
const invitePath = ({ teamId, inviteId }: InviteRef) => `/api/teams/${teamId}/invitations/${inviteId}`

export const useResendInvite = () =>
  useTeamMutation(async (v: InviteRef) => {
    await api.post(`${invitePath(v)}/resend`)
  })

export const useEditInviteEmail = () =>
  useTeamMutation(async ({ email, ...v }: InviteRef & { email: string }) => {
    await api.patch(invitePath(v), { email })
  })

export const useCancelInvite = () =>
  useTeamMutation(async (v: InviteRef) => {
    await api.delete(invitePath(v))
  })
